import { lineupSpans, liveSpanOf } from '@apm/contracts';
import type {
  AdPolicy,
  LineupSpan,
  LiveEventPhase,
  LiveEventSlot,
  LiveEventWindow,
} from '@apm/contracts';

/**
 * 시각축 편성표의 **좌표** (v27, `docs/55` §3 · 절대 규칙 73).
 *
 * ── 왜 `epg.ts` 와 갈라 두는가 ────────────────────────────────
 *
 * 저기는 **「무엇을 보여주고 무엇을 부르는가」**의 판정(`resolveEpgPick`·`toEpgRows`)이고
 * 여기는 **좌표**다. 섞으면 v25 가 물고 있는 그 테스트들이 배율 하나 바꿀 때마다 함께
 * 흔들린다.
 *
 * ── ★ 좌표계가 하나다 ───────────────────────────────────────
 *
 * 모든 x 는 **「지금으로부터 몇 초」**이고, 화면은 그것을 매 초 다시 센다.
 *
 * ```
 *   x(t) = HEAD_W + (t - T_LEFT) × PX_PER_SEC        T_LEFT = -EPG_PAST_MIN×60
 *
 *   「지금」 선        t = 0            → 화면에서 절대 안 움직인다
 *   눈금(절대 시각 T)  t = T - now      → now 가 커지므로 왼쪽으로 흐른다
 *   안 보는 행의 상자  t = span         → span 이 안 변하므로 안 움직인다
 *   재생 중인 행의 상자 t = span - 흐른 초 → 왼쪽으로 흐른다
 * ```
 *
 * **분기가 없다.** 「흐르는가」는 `if` 가 아니라 **그 값이 절대 시각을 기준으로
 * 정의됐는가**에서 저절로 나온다.
 *
 * ── DOM 을 참조하지 않는다 ──────────────────────────────────
 *
 * `tv-focus/` 와 같은 방침이다(`docs/25` D7). 여기는 eslint 가 막아 주지 않으므로
 * **사람이 지킨다** — 판정이 DOM 을 읽기 시작하면 이 파일의 테스트는 초록인 채 실제
 * 동작만 달라진다.
 */

/**
 * 1분에 몇 px. **이 확장의 유일한 조율 손잡이다** (`docs/55` D4).
 *
 * ★ **화면 폭에서 파생하지 않는다.** 「30분이 보이게」로 정하면 고정 열을 뺀 가용 폭이
 * 노트북 1008px · 폰 334px 이라 **같은 2분짜리 상품이 67px 와 22px** 가 된다. 22px 에는
 * 글자가 한 자도 안 들어가고, 그것은 이 화면이 하려는 일이 아니다.
 * **가독성은 절대값이다** — 보이는 시간 폭이 화면마다 다른 쪽을 받는다.
 *
 * ── ★★ 36 이었다가 54 가 됐다 (2026-09-03, 사람이 화면을 보고) ──
 *
 * 홈쇼핑 채널은 상품이 90~120초라 36px/분에서 상자가 **54~72px** 이었고, 제목이
 * 「SIA…」·「롯데 손…」으로 잘려 **무엇인지 알 수 없었다.** 눈금 하나(5분)를 180px 에서
 * **270px** 로 늘려 그 상자가 **81~108px** 이 됐다.
 *
 * **대가는 보이는 시간 폭이다** — 1280px 화면에서 28분 → **18.7분**. 그것이 이 상수의
 * 유일한 저울이고, **둘을 동시에 얻을 수 없다.**
 */
export const EPG_PX_PER_MIN = 54;

/** 축의 왼쪽 끝 = 지금 − 5분. 재생 중인 행의 「이미 본 구간」이 여기 뻗는다 */
export const EPG_PAST_MIN = 5;

/** 눈금 간격(분) */
export const EPG_TICK_MIN = 5;

/** 캔버스가 덮는 앞쪽 시간(분). 이만큼은 스크롤로 닿을 수 있다 */
export const EPG_FILL_MIN = 60;

/** 한 행의 상자 상한 — 짧은 편성이 창을 채우려고 무한히 반복되지 않게 */
export const EPG_MAX_BOXES = 32;

/**
 * 둘째 줄(회차 정보)을 붙일 최소 상자 폭 (`docs/55` §1.3 · D18).
 *
 * 132px 는 **3.7분**이다 — 데모에서 드라마 회차(5분+광고2분 = 252px)는 얻고 홈쇼핑
 * 상품(2분 = 72px)은 못 얻는다. 「S1 · 3회」가 12px 글자로 약 52px 이고, 제목이 여섯
 * 자쯤 남으려면 그만큼이 더 있어야 한다.
 */
export const EPG_LABEL_MIN_PX = 132;

/** 앵커를 다시 잡는 어긋남 문턱(초). 버퍼링·스킵·`resync()` 가 이것을 만든다 */
const REANCHOR_GAP_SEC = 3;

export const EPG_PX_PER_SEC = EPG_PX_PER_MIN / 60;

/** 캔버스가 덮는 구간. `t` 의 정의역이다 */
export const EPG_T_LEFT_SEC = -EPG_PAST_MIN * 60;
export const EPG_T_RIGHT_SEC = EPG_FILL_MIN * 60;

/** 「지금」 기준 초 → 트랙 안의 px. **고정 열은 CSS 가 더한다** */
export function xOf(tSec: number): number {
  return (tSec - EPG_T_LEFT_SEC) * EPG_PX_PER_SEC;
}

/** 트랙의 전체 폭(px) */
export const EPG_TRACK_WIDTH_PX = xOf(EPG_T_RIGHT_SEC);

/** 눈금 하나 */
export interface EpgTick {
  /** 그 눈금의 절대 시각. 라벨을 만드는 값이다 */
  epochMs: number;
  /** 「지금」 기준 초. 라벨과 세로선이 **같은 이 값**에서 나온다 (`docs/55` §9.3) */
  atSec: number;
}

/**
 * 창 안의 **round 5분 눈금들**.
 *
 * ★ **라벨과 세로선을 같은 배열이 낸다.** 세로선을 배경 그라디언트로 그리고 라벨만
 * 엘리먼트로 두면 위치를 두 번 계산하게 되고, 어긋나면 「라벨이 선에서 조금 비켰다」가
 * 된다 — **작아서 아무도 신고하지 않는다.**
 *
 * `now` 가 커지면 각 눈금의 `atSec` 이 줄어든다 — **그래서 눈금이 왼쪽으로 흐른다.**
 */
export function tickTimes(nowMs: number): EpgTick[] {
  const stepMs = EPG_TICK_MIN * 60_000;
  const leftMs = nowMs + EPG_T_LEFT_SEC * 1000;
  // 창의 왼쪽 끝 **이후**의 첫 round 눈금부터.
  const firstMs = Math.ceil(leftMs / stepMs) * stepMs;
  const ticks: EpgTick[] = [];
  for (let at = firstMs; at <= nowMs + EPG_T_RIGHT_SEC * 1000; at += stepMs) {
    ticks.push({ epochMs: at, atSec: (at - nowMs) / 1000 });
  }
  return ticks;
}

/**
 * 앵커 시각의 배치를 지금으로 끌어온다. **왼쪽으로 흐르는 것이 이 뺄셈 하나다.**
 *
 * ★ **매 초 `getElapsedSec()` 을 읽어 다시 세지 않는다** (절대 규칙 73). 광고 중에 그
 * 값은 **광고의 초**라(`resume-return.ts`) 행이 통째로 튄다.
 *
 * **그런데 이 뺄셈은 광고 구간에서도 맞는다** — `lineupSpans` 의 벽시계 산술이 남은
 * 미드롤을 이미 세기 때문이다. 미드롤 하나(mid 초)가 끝나면 흐른 벽시계도 mid 이고,
 * 다시 센 남은 벽시계도 정확히 mid 만큼 줄어 있다 (`docs/55` §5.2).
 */
export function shiftSpans(spans: readonly LineupSpan[], bySec: number): LineupSpan[] {
  if (bySec === 0) return [...spans];
  return spans.map((span) => ({
    ...span,
    startSec: span.startSec - bySec,
    endSec: span.endSec - bySec,
  }));
}

/**
 * 앵커를 (다시) 잡을 것인가. **갈래 넷이고 순서가 계약이다.**
 *
 * **화면에 `if` 로 쓰지 않는다** — 여기 두어야 node 테스트가 넷을 다 본다
 * (`resume-return.ts` 의 `shouldResync` 가 세운 그 방침).
 *
 * ── ★ 광고 검사가 **맨 앞**이다 (2026-09-03 에 값을 치르고 배웠다) ──
 *
 * 처음에는 화면이 `anchor === null || shouldReanchor(...)` 로 불렀고, 그래서 **첫 앵커가
 * 이 함수를 통째로 건너뛰었다.** 진입 프리롤(드라마 채널은 15초다) 중에 그 일이
 * 일어나면 `getElapsedSec()` 이 **광고의 초**라, 200초에서 이어보는 회차의 「지금」이
 * **2초**로 그려진다.
 *
 * 실측 (드라마 채널 · 이어보기 200초 · `preRollSec: 15`):
 *
 * | 언제 | 걸치는 지점 |
 * |---|---|
 * | 프리롤 중 | **3초 · 6초 · 6초** ← 회차의 맨 앞이다 |
 * | 프리롤 뒤 | **240초** ← 참값(200 + 지나온 미드롤 30) |
 *
 * **237초가 한 번에 튄다** — 화면에서 213px 이고, 채널을 오갈 때마다 프리롤을 지나므로
 * **오갈 때마다 흔들린다.** 사람이 무대에서 그렇게 신고했다.
 *
 * 그래서 **`inAdBreak` 이면 처음이든 다시든 잡지 않는다.** 앵커가 없는 동안 행은
 * **서버가 준 이어보기 지점**으로 그려지는데, 프리롤 중에는 회차가 아직 한 프레임도
 * 안 갔으므로 **그것이 정확히 참이다.**
 *
 * 회차가 바뀐 경우도 광고 뒤로 미룬다 — 회차 사이 브레이크의 초는 **벽시계 산술이 이미
 * 세고 있어서**(`betweenEpisodesSec`) 옛 앵커를 그대로 흘려도 경계가 제때 「지금」에
 * 닿는다 (`docs/55` §5.2).
 */
export function shouldReanchor(input: {
  anchorProductId: string | null;
  currentProductId: string | null;
  /** 광고 구간이면 `getElapsedSec()` 이 **광고의 초**라 비교가 애초에 성립하지 않는다 */
  inAdBreak: boolean;
  /**
   * v33. 바이패스 구간이면 그 값이 **라이브의 초**다 — 같은 사정이고 **어긋남이 훨씬
   * 크다**(창이 두 시간이다). 그대로 믿으면 행이 통째로 튄다(TG14 가 광고에서 겪은 그것).
   *
   * ★ **`inAdBreak` 과 따로 받는다**(`docs/67` D25) — 이유가 같아도 **끊는 자리는 둘이다.**
   * 한 칸으로 겸하면 이름이 거짓이 되고, 언젠가 「광고인가」를 묻는 쪽이 라이브에서 참을 받는다.
   */
  inLive: boolean;
  /** 뺄셈이 말하는 지금의 콘텐츠 초 */
  predictedElapsedSec: number;
  /** 리시버가 말하는 지금의 콘텐츠 초 */
  actualElapsedSec: number;
}): boolean {
  const { anchorProductId, currentProductId, inAdBreak, inLive } = input;
  if (currentProductId === null) return false;
  // ★ 광고 중에는 잡지 않는다 — **처음이든 다시든.** 위 ★ 를 읽을 것.
  if (inAdBreak) return false;
  // v33. 바이패스도 같다 — 그때 그 행은 **컷이 일어난 자리에서 멈춰 있어야 한다**(§9.2).
  if (inLive) return false;
  // 회차가 바뀌었다(앵커가 아예 없는 것도 여기다) — 잡는다.
  if (anchorProductId !== currentProductId) return true;
  return Math.abs(input.predictedElapsedSec - input.actualElapsedSec) > REANCHOR_GAP_SEC;
}

/** 재생 중인 행의 기준점 — **이 초가 이 시각에 「지금」 선에 있었다** */
export interface EpgAnchor {
  productId: string;
  elapsedSec: number;
  atMs: number;
}

/**
 * 매 초 틱의 앵커. **화면은 이것의 반환값을 그대로 담는다** — 흐름은 `atMs` 에서 지금까지의
 * 뺄셈 하나다(절대 규칙 73 ③).
 *
 * ── ★ v33-d. 바이패스 중에는 녹화 칸이 **멈춘다** (2026-09-23 무대 신고) ──
 *
 * 「채널 5 의 녹화물이 라이브 중에도 편성표에서 함께 흘러간다.」 `shouldReanchor` 가
 * 바이패스에서 **다시 잡지 않는 것**까지는 했는데, 앵커를 **그대로 두면** 화면의 뺄셈
 * (`지금 − atMs`)이 계속 커져 **행이 왼쪽으로 계속 흘렀다.** 녹화물은 컷에서 멈춰 있는데
 * 편성표는 재생 중처럼 그렸다 — 규칙 73 ②(「재생 중인 행만 흐른다」)의 위반이다.
 *
 * 그래서 바이패스 중에는 **매 틱 「지금」에 다시 박는다** — 돌아갈 초(`liveResumeAtSec` ·
 * 리시버의 `returnTo`)가 언제나 「지금」 선에 있고, 뺄셈은 0 이다. 창이 끝나 돌아오면 그 초에서
 * 다시 흐르기 시작하고, `shouldReanchor` 의 예측(`그 초 + 흐른 시간`)이 실제와 맞으므로 **튀지
 * 않는다.**
 *
 * 돌아갈 초가 없으면(창 중간에 들어왔다 · D8) **앵커를 버린다** — 그때 행은 서버가 준
 * 그대로 그려지고, 그것은 안 보는 행처럼 이미 멈춰 있다.
 */
export function nextEpgAnchor(
  anchor: EpgAnchor | null,
  playing: {
    productId: string | null;
    elapsedSec: number;
    inAdBreak: boolean;
    inLive: boolean;
    /** 바이패스에서 돌아갈 콘텐츠 초(`LiveView.resumeAtSec`). 바이패스가 아니면 `null` */
    liveResumeAtSec: number | null;
    /** v35. 시청자가 멈췄는가 — 아래 ★ v35 */
    paused: boolean;
  } | null,
  atMs: number,
): EpgAnchor | null {
  if (playing === null || playing.productId === null || playing.productId === '') return null;
  const productId = playing.productId;
  if (playing.inLive) {
    return playing.liveResumeAtSec === null
      ? null
      : { productId, elapsedSec: playing.liveResumeAtSec, atMs };
  }
  /*
   * ★ v35. **정지한 행도 멈춘다** (`docs/71` §2.6 · 절대 규칙 73 ②). 바이패스와 같은 모양이다 —
   * 매 틱 「지금」에 다시 박아 뺄셈을 0 으로 둔다. 그대로 두면 벽시계가 행을 흘리다 어긋남이
   * 3초(`REANCHOR_GAP_SEC`)를 넘을 때마다 되잡혀 **행이 3초마다 뒤로 튄다.** 재개하면 그 초에서
   * 다시 흐르고 예측이 실제와 맞아 튀지 않는다. 광고 중에는 정지가 없다(코어가 받지 않는다).
   */
  if (playing.paused && !playing.inAdBreak) {
    return { productId, elapsedSec: playing.elapsedSec, atMs };
  }
  /*
   * ★ **`anchor === null` 로 이 판정을 건너뛰지 말 것** (2026-09-03). 그러면 **첫 앵커가
   * 광고 구간을 통과하고**, 진입 프리롤 중에 `getElapsedSec()` 이 광고의 초라 200초에서
   * 이어보는 회차의 「지금」이 2초로 그려진다 — 프리롤이 끝나면 **237초가 한 번에 튄다.**
   */
  const predicted = anchor === null ? Number.NaN : anchor.elapsedSec + (atMs - anchor.atMs) / 1000;
  const reanchor = shouldReanchor({
    anchorProductId: anchor?.productId ?? null,
    currentProductId: productId,
    inAdBreak: playing.inAdBreak,
    inLive: playing.inLive,
    predictedElapsedSec: predicted,
    actualElapsedSec: playing.elapsedSec,
  });
  return reanchor ? { productId, elapsedSec: playing.elapsedSec, atMs } : anchor;
}

/**
 * 편성표 상자 하나. **ONAIR 행만 브레이크의 자리를 안다**(`OnairSlot.breaks` · 서버가 이어 놓았으니 확정이다).
 * 개인화 행은 `breakSec` 몫만 알고 꼬리에 그린다 — 미드롤의 자리는 재생이 정하고 편성표가 미리 말하지 않는다.
 */
export interface EpgSpan extends LineupSpan {
  /** 「지금」 기준 초 · 시각 순. 없으면 `breakSec` 을 꼬리로 그린다 */
  breaks?: readonly { startSec: number; endSec: number }[];
}

/** 상자 하나가 트랙에서 차지하는 자리 */
export interface EpgBoxGeometry {
  /** 트랙 안 px */
  leftPx: number;
  widthPx: number;
  /** 「이미 본 구간」의 px. 0 이면 안 그린다 */
  seenPx: number;
  /** 브레이크들의 자리(상자 왼쪽 끝 기준 px). 비면 안 그린다 */
  breaks: { leftPx: number; widthPx: number }[];
  /** 왼쪽이 창 밖이라 잘렸는가 — 제목을 왼쪽 끝에 붙이는 근거다 */
  clippedLeft: boolean;
}

/**
 * `.epg__box` 가 더 좁아지지 못하는 폭(px).
 *
 * `padding: 0.25rem 0.4rem` 의 좌우 합(0.8rem = 12.8px)이고 `box-sizing: border-box` 다 —
 * **CSS 의 사실이라 두 곳에 산다.** 저쪽 패딩을 바꾸면 이 값도 바꾼다. 안 바꾸면 증상은
 * 「가끔 상자가 옆을 조금 덮는다」이고, **사람 눈에는 안 보이고 하네스만 본다.**
 */
export const EPG_BOX_FLOOR_PX = 12.8;

/**
 * 상자의 자리와 폭. **창 밖이면 `null` 이고 그리지 않는다.**
 *
 * ★ **`min-width` 를 주지 않는다** (절대 규칙 73). 폭이 시간과 안 맞으면 **뒤가 전부
 * 밀리고**, 짧은 항목이 많은 홈쇼핑에서 가장 크게 틀린다 — 그런데 **읽기는 더 쉬워진다.**
 * 축이 진실이고 글자가 양보한다.
 */
export function boxGeometry(span: EpgSpan): EpgBoxGeometry | null {
  if (span.endSec <= EPG_T_LEFT_SEC || span.startSec >= EPG_T_RIGHT_SEC) return null;
  const clippedLeft = span.startSec < EPG_T_LEFT_SEC;
  const clippedRight = span.endSec > EPG_T_RIGHT_SEC;
  const from = clippedLeft ? EPG_T_LEFT_SEC : span.startSec;
  const to = Math.min(span.endSec, EPG_T_RIGHT_SEC);
  const leftPx = xOf(from);
  const widthPx = xOf(to) - leftPx;
  // 「이미 본 구간」은 상자의 왼쪽 끝부터 「지금」 선까지다. 0 보다 뒤에서 시작하면 없다.
  const seenPx = span.startSec >= 0 ? 0 : xOf(Math.min(0, to)) - leftPx;
  /*
   * 브레이크는 자리를 아는 행(ONAIR · `breaks`)이면 제자리에, 모르는 행(개인화)이면 꼬리에 몫만큼 칠한다.
   * 창 밖으로 잘린 만큼은 버린다.
   */
  const ranges =
    span.breaks ??
    (span.breakSec > 0 ? [{ startSec: span.endSec - span.breakSec, endSec: span.endSec }] : []);
  const breaks = ranges.flatMap((range) => {
    const start = Math.max(range.startSec, from);
    const end = Math.min(range.endSec, to);
    return end > start ? [{ leftPx: xOf(start) - leftPx, widthPx: xOf(end) - xOf(start) }] : [];
  });
  /*
   * ★★ **바닥보다 좁아진 잘린 상자는 그리지 않는다** (2026-09-05, `verify-epg` 가 잡았다).
   *
   * `.epg__box` 는 `box-sizing: border-box` 에 좌우 패딩이 `0.4rem` 씩이라 **12.8px 아래로는
   * 줄지 않는다** — 그보다 좁게 시키면 **시킨 것보다 넓게 그려져 옆 상자를 덮는다.**
   * `epg.boxWidthMatchesWallClock` 이 그것을 4.2px·2.2px 로 잡았고(그 검사는 `Math.abs`
   * 라 겹침도 센다), 같은 실행의 `minW` 가 정확히 **12.8** 이었다.
   *
   * **`min-width` 를 주는 것과 반대 방향이다**(절대 규칙 73) — 넓히지 않고 **버린다.**
   * 버리는 것은 **창 밖으로 나가는(또는 들어오는) 상자뿐**이라, 그 몇 초는 어차피
   * 가장자리에서 사라지는 중이다. **안 잘린 상자는 아무리 짧아도 그린다** — 그것은
   * 진짜로 짧은 항목이고, 조금 겹치더라도 **없는 것보다 낫다.**
   */
  if ((clippedLeft || clippedRight) && widthPx < EPG_BOX_FLOOR_PX) return null;
  return {
    leftPx,
    widthPx,
    seenPx: Math.max(0, seenPx),
    breaks,
    clippedLeft,
  };
}

/**
 * 상자 안에 회차 정보 줄을 붙일 것인가 (`docs/55` §1.3 · D18).
 *
 * ★ **CSS 컨테이너 질의로 하지 않는다.** 하면 **판정이 node 테스트 밖으로 나가고**
 * (`docs/25` D7 이 막아 둔 그 자리), 문턱을 고칠 때 검사가 아무 말도 하지 않는다.
 *
 * ★★ **문턱을 못 넘은 상자에서 「작게라도」 그리지 않는다.** 제목이 밀려 **두 줄 다 못
 * 읽는다** — 좁은 상자에서 읽을 수 있는 것은 하나뿐이고 그것은 제목이다.
 */
export function boxDensity(widthPx: number): 'title' | 'titleAndLabel' {
  return widthPx >= EPG_LABEL_MIN_PX ? 'titleAndLabel' : 'title';
}

/**
 * 포커스된 상자가 **고정 열에 파묻힌 만큼**(px). 0 이면 손댈 것이 없다.
 *
 * ── ★ 왜 CSS 로 안 되는가 (2026-09-03 실측) ──────────────────
 *
 * `scroll-padding-inline-start` 는 **스크롤이 일어날 때만** 듣는다. 상자가 스크롤포트
 * **안**에 있으면 브라우저는 「보인다」고 판단해 **스크롤을 아예 하지 않고**, 고정 열이
 * 그것을 덮고 있다는 것을 모른다 — 실측에서 `scrollLeft` 가 한 픽셀도 안 움직였다.
 * `scroll-margin-inline-start` 로 바꿔도, 둘을 함께 줘도 **같은 값**이었다.
 *
 * **그래서 자물쇠가 둘이다** — `scroll-padding` 은 **스크롤이 일어나는 경우**(상자가
 * 스크롤포트 밖)를 맡고(지웠을 때 −195px 였다), 이 함수는 **안 일어나는 경우**를 맡는다.
 *
 * 배율을 36 → 54 로 올리자 뒤엣것이 드러났다 — 36 에서 초록이던 것은 **기하가 우연히
 * 비켜 있었기 때문**이고, 그때도 이 구멍은 있었다.
 */
export function hiddenUnderHeadPx(input: {
  /** 포커스된 상자의 왼쪽 (viewport) */
  boxLeft: number;
  /** 스크롤포트의 왼쪽 (viewport) */
  portLeft: number;
  /** 고정 열의 폭 */
  headWidthPx: number;
}): number {
  return Math.max(0, input.portLeft + input.headWidthPx - input.boxLeft);
}

/** 한 행의 상자들. 계약의 산술을 그대로 부르고 여기서 다시 쓰지 않는다 (절대 규칙 57) */
export function rowSpans(
  policy: AdPolicy | null,
  items: readonly { durationSec: number }[],
  resumePositionSec: number | null,
): LineupSpan[] {
  return lineupSpans(policy, items, resumePositionSec, {
    untilSec: EPG_T_RIGHT_SEC,
    maxSpans: EPG_MAX_BOXES,
  });
}

/**
 * ONAIR 행의 상자들 (v28, `docs/57` §7.1 · 절대 규칙 73·75).
 *
 * ── ★ 분기가 없다 ───────────────────────────────────────────
 *
 * v27 이 「흐르는가는 `if` 가 아니라 **그 값이 절대 시각 기준으로 정의됐는가**에서
 * 나온다」로 좌표계를 세워 둔 덕에, ONAIR 슬롯은 **절대 시각을 「지금」 기준 초로 바꾸기만
 * 하면** 저절로 흐른다. `xOf`·`boxGeometry`·`tickTimes`·`boxDensity` 는 한 줄도 안 바뀐다.
 *
 * ── 개인화 행과 갈리는 것 둘 ────────────────────────────────
 *
 * ① **`breakSec` 이 0 이다** — ONAIR 채널에는 광고가 없다(v28 범위 · 절대 규칙 76).
 * ② **`cycle` 을 세지 않는다** — 서버가 이미 창을 채워 보냈고, 되돌아온 바퀴도 그냥
 *    다음 슬롯이다. 화면이 「몇 바퀴째인가」를 알 이유가 없다.
 *
 * ── 「지금」 상자의 판정 ─────────────────────────────────────
 *
 * **`cycle 0 · index 0` 이 아니다**(그것은 개인화 행의 규칙이다 — 절대 규칙 68).
 * ONAIR 에서 「지금」은 **`startSec <= 0 < endSec`** 인 상자이고, 그것 하나만 누를 수 있다
 * (`docs/57` §7.3). 창의 왼쪽이 과거로 채워져 있으므로 `index 0` 은 **이미 지나간 것**이다.
 */
export function onairSpansAt(
  slots: readonly {
    startAtMs: number;
    endAtMs: number;
    breakMs?: number;
    breaks?: readonly { offsetMs: number; durationMs: number }[];
  }[],
  nowMs: number,
): EpgSpan[] {
  return slots.map((slot, index) => ({
    index,
    cycle: 0,
    startSec: (slot.startAtMs - nowMs) / 1000,
    endSec: (slot.endAtMs - nowMs) / 1000,
    /*
     * v30. **회차 뒤에 붙는 광고**(`docs/60`). 개인화 편성의 `breakSec` 과 **같은 뜻**이라
     * 상자를 그리는 코드가 한 줄도 안 바뀐다 — `boxGeometry` 가 이 값으로 꼬리에 빗금을
     * 친다. 없으면 0 이고 지금까지와 같다.
     */
    breakSec: (slot.breakMs ?? 0) / 1000,
    /*
     * v38. **브레이크의 자리** — `breakMs` 는 몫만 말해 회차 안 중간 광고도 꼬리에 그려졌다. 없던 판의 서버면
     * `undefined` 그대로 두어 꼬리로 그린다.
     */
    breaks: slot.breaks?.map((b) => ({
      startSec: (slot.startAtMs + b.offsetMs - nowMs) / 1000,
      endSec: (slot.startAtMs + b.offsetMs + b.durationMs - nowMs) / 1000,
    })),
  }));
}

/**
 * ONAIR 행에서 **지금 방송 중인** 상자의 자리. 없으면 `-1`.
 *
 * ★ **화면 위치로 판정하지 않는다** — 눈금 반올림 때문에 경계에서 흔들린다(절대 규칙 68 의
 * 그 단서). 이 값 하나가 「누를 수 있는 상자」와 「지금 방송 중」 배지를 함께 정한다.
 */
export function onairNowIndex(spans: readonly LineupSpan[]): number {
  return spans.findIndex((span) => span.startSec <= 0 && span.endSec > 0);
}

/** v33. 라이브 상자 하나의 자리. `LineupSpan` 과 **같은 좌표계**다 */
export interface EpgLiveSpan {
  slot: LiveEventSlot;
  startSec: number;
  endSec: number;
  /**
   * v34. **상자가 무엇을 말하는가** — `waiting` 은 「개시 대기」(시작단이 지금 선에 붙어 함께
   * 간다), `extended` 는 「연장 중」(끝단이 지금 + 5분), 방송 중이면서 `openEnd` 면 끝단이
   * 「예상 종료」다. 판정은 계약의 `liveSpanOf` 하나다(절대 규칙 86).
   */
  phase: LiveEventPhase;
  openEnd: boolean;
}

/**
 * ★ v33-b. 보이는 **라이브 창 전부** (`docs/67` §9 개정 · 2026-09-22).
 *
 * ── 왜 끼워 넣지 않고 따로 내는가 ───────────────────────────
 *
 * 처음 판은 라이브 창을 녹화축에 **끼워 넣었다**(`spliceLiveWindow` — 녹화물을 자르고
 * 꼬리를 창 길이만큼 뒤로 민다). 한 줄에 두 축을 담으려면 그 수밖에 없었고, **바이패스로
 * 라이브를 보고 있는 동안에는 그것이 참이었다** — 녹화물은 정말 멈춰 있었으니까.
 *
 * **그런데 시청자는 라이브를 무시할 수 있다**(절대 규칙 85 — 녹화 상자 선택은 산다).
 * 그러면 같은 시각에 두 사실이 **동시에** 참이 된다: 「라이브는 11분째 진행 중」이고
 * 「Ep3 는 2분 30초째 재생 중」이다. **한 줄로는 둘 중 하나밖에 말할 수 없고**, 끼워
 * 넣은 줄은 **지금 재생 중인 회차를 창이 끝난 뒤로 밀어** 놓는다 — 무대에서 「고른
 * 콘텐츠는 나오는데 편성표의 시점이 틀리다」로 신고됐다(2026-09-22).
 *
 * 그래서 **줄을 위아래로 가른다.** 위는 라이브(절대 시각 · 언제나 흐른다), 아래는 녹화
 * (순서축 · 재생 중일 때만 흐른다). 「지금」 선 하나가 둘을 동시에 지난다 — 그것이
 * 시청자가 보는 두 지점이다.
 *
 * ── 이 함수가 하는 일은 하나다 ──────────────────────────────
 *
 * **녹화 좌표에 손대지 않는다.** 창을 「지금」 기준 초로 바꿔 **보이는 것만** 고른다 —
 * ONAIR 슬롯(`onairSpansAt`)과 **같은 모양**이고, 그래서 저절로 흐른다(규칙 73 의 좌표계).
 *
 * **상자 상한(`EPG_MAX_BOXES`)을 두지 않는다** — 채널당 창이 여덟을 넘지 못하고
 * (`LIVE_EVENT_MAX_WINDOWS`) 위 걸러내기가 한 번 더 좁힌다. 걸릴 수 없는 가드는 검사할
 * 수 없고, 검사할 수 없는 줄은 **틀려도 아무도 모른다.**
 *
 * **하나가 아니라 전부다.** 데모 편성이 10분짜리를 30분마다 두므로 한 화면(65분)에 창이
 * 둘셋 들어온다 — 하나만 그리면 **다음 창이 없는 것처럼 보인다.**
 */
export function liveSpansAt(slots: readonly LiveEventSlot[], nowMs: number): EpgLiveSpan[] {
  const spans = slots
    .map((slot) => {
      const span = liveSpanOf(slot, nowMs);
      return {
        slot,
        startSec: span.startSec,
        endSec: span.endSec,
        phase: span.phase,
        openEnd: span.openEnd,
      };
    })
    // 시작이 같으면 **열린 것(방송 중·연장 중)이 앞이다** — 밀리는 쪽은 아직 안 열린 것이다.
    .sort((a, b) => a.startSec - b.startSec || openRank(a.phase) - openRank(b.phase));
  /*
   * ★ v34-c. **한 칸에 둘이 겹치지 않게 앞 상자의 끝 뒤로 민다** (2026-09-24 무대 · `docs/70` §7).
   *
   * 서버는 **예정 창끼리만** 겹침을 막는다(`LIVE_EVENT_OVERLAP`). 그런데 개시 대기의 시작단(지금)과
   * 연장의 끝단(지금 + 5분)은 **움직여서** 이웃의 예정 창으로 넘어간다 — 무대에서 연장 중인 상자와
   * 개시 대기 상자가 5분 동안 포개져 제목끼리 덮였다. **미는 것이 사실과도 맞다**: 큐 개시는 앞 창이
   * 열려 있는 동안 409 이므로 뒤 것은 앞 것이 끝나야 열린다(시각 개시가 섞인 편성의 겹침 · R3 은
   * 리시버가 먼저 시작한 것을 틀므로 그림도 그것을 앞에 둔다).
   *
   * **밀어서 폭이 없어진 상자는 그리지 않는다** — 앞 것이 끝나야 열 수 있는데 그 전에 예정 종료에
   * 닿아 만료된다(v34-c · 절대 규칙 86). 앞 것이 일찍 끝나면 다음 틱에 다시 선다.
   *
   * 깨 본 기록(2026-09-24): 밀기를 빼면(옛 판) 새 검사 셋(⑨ 의 v34-c)이 모두 빨갛다 — 무대의 순간은
   * B 가 `startSec: 0`(지금 선에 포갠다)이다.
   */
  const laid: EpgLiveSpan[] = [];
  let floorSec = Number.NEGATIVE_INFINITY;
  for (const span of spans) {
    const startSec = Math.max(span.startSec, floorSec);
    if (startSec >= span.endSec) continue;
    laid.push(startSec === span.startSec ? span : { ...span, startSec });
    floorSec = Math.max(floorSec, span.endSec);
  }
  return laid.filter((s) => s.endSec > EPG_T_LEFT_SEC && s.startSec < EPG_T_RIGHT_SEC);
}

/** 정렬의 타이브레이크 — 열린 창 · 대기 · 예정 · 끝남 */
function openRank(phase: EpgLiveSpan['phase']): number {
  return phase === 'running' || phase === 'extended' ? 0 : phase === 'waiting' ? 1 : 2;
}

/**
 * ★ v34. **리시버의 창 → 편성표의 상자** — 지금 채널의 행을 1초 조회의 새 값으로 그린다
 * (`docs/69` §7.2 · 절대 규칙 87).
 *
 * **필드를 하나씩 적는다 — 스프레드하지 않는다**(`liveSlotsOf` 의 ★). 창은 재생 경로의 값이라
 * `streamUrl` 이 있고, 옮기면 조용히 따라 들어와 **편성표가 리시버를 우회해 라이브를 틀 수 있는
 * 주소를 갖게 된다**(규칙 68). 화면은 아무 차이 없이 잘 돈다 — 그래서 여기서 막는다.
 */
export function liveSlotsFromWindows(windows: readonly LiveEventWindow[]): LiveEventSlot[] {
  return windows.map((w) => ({
    eventId: w.eventId,
    title: w.title,
    startAtMs: w.startAtMs,
    endAtMs: w.endAtMs,
    startMode: w.startMode,
    endMode: w.endMode,
    actualStartAtMs: w.actualStartAtMs,
    actualEndAtMs: w.actualEndAtMs,
  }));
}

/**
 * 2026-10-29 · **라이브 이벤트 앞뒤 광고의 띠**(`docs/45` §18) — 혼합 채널 행의 라이브 칸에, 상자의 앞뒤에 붙여 그린다(상자가 아니다 ·
 * 누를 수 없고 포커스 후보가 아니다). 자리는 리시버가 그 광고를 트는 때와 같다.
 *
 * - **시작 전**: 시각 방식은 시작 **앞** `beforeSec`(시작 순간에 끝나게 자른다). 개시 큐는 시작 시각을 모르므로 큐가 온 뒤 상자의 **머리**
 *   `beforeSec`(큐 뒤 광고를 먼저 틀고 합류한다) · 큐를 기다리는 동안은 없다.
 * - **종료 후**: 끝 **뒤** `afterSec`(열린 종료면 지금 보이는 끝 — 연장되면 함께 밀린다).
 *
 * 창 밖으로 잘린 만큼은 버린다. 폭이 0 이면 내지 않는다.
 */
export function liveAdStripes(
  live: EpgLiveSpan,
  beforeSec: number,
  afterSec: number,
): { kind: 'before' | 'after'; leftPx: number; widthPx: number }[] {
  const ranges: { kind: 'before' | 'after'; startSec: number; endSec: number }[] = [];
  if (beforeSec > 0) {
    if (live.slot.startMode === 'time') {
      ranges.push({ kind: 'before', startSec: live.startSec - beforeSec, endSec: live.startSec });
    } else if (live.phase !== 'waiting' && live.phase !== 'scheduled') {
      ranges.push({
        kind: 'before',
        startSec: live.startSec,
        endSec: Math.min(live.endSec, live.startSec + beforeSec),
      });
    }
  }
  if (afterSec > 0)
    ranges.push({ kind: 'after', startSec: live.endSec, endSec: live.endSec + afterSec });
  return ranges.flatMap((range) => {
    const start = Math.max(range.startSec, EPG_T_LEFT_SEC);
    const end = Math.min(range.endSec, EPG_T_RIGHT_SEC);
    if (end <= start) return [];
    return [{ kind: range.kind, leftPx: xOf(start), widthPx: xOf(end) - xOf(start) }];
  });
}
